import { emitFlagMessage } from '@cae/cobalt-react/components/flag-message';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { useNavigationBlock } from '@shared/hooks/use-navigation-block/useNavigationBlock';
import * as React from 'react';

import type {
  CitizenshipFormState,
  UseEditCitizenshipPanelOptions,
  UseEditCitizenshipPanelReturn,
} from './useEditCitizenshipPanel.types';

import { useCountries } from '../use-countries/useCountries';
import { useNationalities } from '../use-nationalities/useNationalities';
import { useProfile } from '../use-profile/useProfile';
import { useToggletip } from '../use-toggletip/useToggletip';
import { useUpdateProfileCitizenship } from '../use-update-profile-citizenship/useUpdateProfileCitizenship';
import { buildInitialCitizenshipFormState } from './useEditCitizenshipPanel.utils';

export function useEditCitizenshipPanel({
  onClose,
}: UseEditCitizenshipPanelOptions): UseEditCitizenshipPanelReturn {
  const { profile, isProfileLoading, isProfileError } = useProfile();
  const { nationalities, isNationalitiesLoading, isNationalitiesError } =
    useNationalities(true);
  const { countries, isCountriesLoading, isCountriesError } = useCountries();
  const { mutate, isPending, isError, isSuccess } =
    useUpdateProfileCitizenship();
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const citizenship = useToggletip();
  const residency = useToggletip();

  const isOptionsLoading = isNationalitiesLoading || isCountriesLoading;
  const isOptionsError = isNationalitiesError || isCountriesError;

  const [isExitModalOpen, setIsExitModalOpen] = React.useState<boolean>(false);

  const [formState, setFormState] = React.useState<CitizenshipFormState>(() =>
    buildInitialCitizenshipFormState(profile)
  );

  const initialized = React.useRef(false);
  const initialFormStateRef = React.useRef<CitizenshipFormState>(formState);
  const isPageRefreshingRef = React.useRef(false);
  const isDiscardingRef = React.useRef(false);

  React.useEffect(() => {
    const handleBeforeUnload = (): void => {
      isPageRefreshingRef.current = true;
    };
    globalThis.addEventListener('beforeunload', handleBeforeUnload);
    return (): void => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  React.useEffect(() => {
    if (profile && !initialized.current) {
      initialized.current = true;
      const initial = buildInitialCitizenshipFormState(profile);
      initialFormStateRef.current = initial;
      setFormState(initial);
    }
  }, [profile]);

  const handleFieldChange = React.useCallback(
    (field: keyof CitizenshipFormState, value: string): void => {
      setFormState(prev => ({ ...prev, [field]: value }));
    },
    []
  );

  const isDirty =
    !isSuccess &&
    (formState.lastGrantedCitizenship !==
      initialFormStateRef.current.lastGrantedCitizenship ||
      formState.currentPermanentResidency !==
        initialFormStateRef.current.currentPermanentResidency);

  const isDirtyRef = React.useRef(isDirty);
  isDirtyRef.current = isDirty;

  useNavigationBlock(
    React.useCallback(
      (): boolean => isDirtyRef.current && !isDiscardingRef.current,
      []
    )
  );

  const handleSubmit = React.useCallback((): void => {
    if (!isDirtyRef.current || !profile) return;
    mutate(
      {
        lastGrantedCitizenship: formState.lastGrantedCitizenship.trim() || null,
        currentPermanentResidency:
          formState.currentPermanentResidency.trim() || null,
      },
      {
        onSuccess: (): void => {
          isDirtyRef.current = false;
          emitFlagMessage('success', 'Changes saved successfully!', 5000);
          onClose();
        },
      }
    );
  }, [formState, profile, mutate, onClose]);

  const handleCloseAttempt = React.useCallback((): void => {
    if (isPageRefreshingRef.current) return;
    if (isPending) return;
    if (isDirtyRef.current) {
      setIsExitModalOpen(true);
    } else {
      onClose();
    }
  }, [isPending, onClose]);

  const handleExit = React.useCallback((): void => {
    isDiscardingRef.current = true;
    setIsExitModalOpen(false);
    onClose();
  }, [onClose]);

  const handleKeepEditing = React.useCallback((): void => {
    setIsExitModalOpen(false);
  }, []);

  const isProfileReady = !!profile;

  return {
    formState,
    isMobile,
    isExitModalOpen,
    isDirty,
    isPending,
    isError,
    isProfileLoading,
    isProfileError,
    isProfileReady,
    nationalities,
    countries,
    isOptionsLoading,
    isOptionsError,
    citizenshipAnchorEl: citizenship.anchorEl,
    residencyAnchorEl: residency.anchorEl,
    handleFieldChange,
    handleSubmit,
    handleCloseAttempt,
    handleExit,
    handleKeepEditing,
    handleCitizenshipToggletipClick: citizenship.handleClick,
    handleCitizenshipToggletipClose: citizenship.handleClose,
    handleResidencyToggletipClick: residency.handleClick,
    handleResidencyToggletipClose: residency.handleClose,
  };
}
